import VASTError from './vast-error';
import VASTTracker from './vast-tracker';
import VASTParser, { VastUrlMacros } from './vast-parser';
import { templateMacros } from '../../../../common/js/url-utils';
import { RootStore } from '../../../../common/js/stores/root.store';
import { MediaFile } from './media-file';
import { VastIconType } from './vast-icon';

const supportedEvents = [
  'creativeView',
  'start',
  'firstQuartile',
  'midpoint',
  'thirdQuartile',
  'complete',
  'progress',
  'mute',
  'unmute',
  'close',
  'closeLinear',
  'pause',
  'resume',
  'skip'
];

interface Options {
  macros?: Record<string, any>;
  duration?: number;
  muted: boolean;
  vastUrlMacros?: VastUrlMacros;
  timeout?: number;
  timeoutStrategy?: 'exponential' | 'fixed';
  retries?: number;
  retryDelay?: number;
  maxTotalTime?: number;
}

export default class VASTClient {
  _vastParser: VASTParser;
  _vastTracker: VASTTracker;
  _options: Options;

  _realDuration?: number;
  _billedDuration?: number;

  static createClientFromURL(vastURL: string, options: Options): VASTClient {
    const vastParser = new VASTParser(
      supportedEvents,
      options.vastUrlMacros || []
    );
    vastParser.addVastURL(vastURL);
    return VASTClient.createClient(vastParser, options);
  }

  static createClientFromURLAsync(
    vastURL: string,
    options: Options
  ): Promise<VASTClient> {
    const vastParser = new VASTParser(
      supportedEvents,
      options.vastUrlMacros || [],
      options.timeout,
      options.timeoutStrategy,
      options.retries,
      options.retryDelay,
      options.maxTotalTime
    );
    return vastParser.addVastURLAsync(vastURL).then(() => {
      return VASTClient.createClient(vastParser, options);
    });
  }

  static createClientFromContent(
    vastContent: string,
    options: Options
  ): VASTClient {
    const vastParser = new VASTParser(
      supportedEvents,
      options.vastUrlMacros || []
    );
    vastParser.addVastContent(vastContent);
    return VASTClient.createClient(vastParser, options);
  }

  static createClientFromContentAsync(
    vastContent: string,
    options: Options
  ): Promise<VASTClient> {
    const vastParser = new VASTParser(
      supportedEvents,
      options.vastUrlMacros || [],
      options.timeout,
      options.timeoutStrategy,
      options.retries,
      options.retryDelay,
      options.maxTotalTime
    );
    return vastParser.addVastContentAsync(vastContent).then(() => {
      return VASTClient.createClient(vastParser, options);
    });
  }

  static createClient(vastParser: VASTParser, options: Options): VASTClient {
    const vastTracker = new VASTTracker(
      vastParser.getClickTrackingURLTemplates(),
      vastParser.getClickThroughURLTemplate(),
      vastParser.getImpressionURLTemplates(),
      vastParser.getErrorURLTemplates(),
      vastParser.getViewableImpressionURLTemplates(),
      vastParser.getTrackingEvents(),
      vastParser.getMediaFiles(),
      options.duration || vastParser.getDuration(),
      vastParser.getSkipDelay(),
      options.muted,
      options.macros || {},
      vastParser.getVastIcon() as VastIconType,
      vastParser.getApiFramework()
    );

    return new VASTClient(vastParser, vastTracker, options);
  }

  constructor(
    vastParser: VASTParser,
    vastTracker: VASTTracker,
    options: Options
  ) {
    this._vastParser = vastParser;
    this._vastTracker = vastTracker;
    this._options = options;
  }

  getDuration(): number {
    if (!this._realDuration) {
      this._realDuration = this._vastParser.getDuration();
    }
    return this._realDuration;
  }

  getBilledDuration(): number {
    if (!this._billedDuration) {
      this._billedDuration = this._options.duration || this.getDuration();
    }
    return this._billedDuration;
  }

  getAdParameters(): string {
    const adParameters = this._vastParser.getAdParameters();
    const macros = (this._options && this._options.macros) || {};
    return templateMacros(adParameters || '', macros);
  }

  getMediaFiles(): MediaFile[] {
    return this._vastParser.getMediaFiles();
  }

  getVastIcon(): VastIconType | undefined {
    return this._vastParser.getVastIcon() as VastIconType | undefined;
  }

  hasVpaidMediaFile(): boolean {
    return this._vastParser.getApiFramework() === 'VPAID';
  }

  hasViewableImpression(): boolean {
    return this._vastParser.getViewableImpressionURLTemplates().length > 0;
  }

  setMRCViewable(): void {
    this._vastTracker.setMRCViewable();
  }

  getJSVerificationResources(): any[] {
    let verificationResources = this._vastParser.getJSVerificationResources();
    const macros = (this._options && this._options.macros) || {};

    // Replace macros in verification resources and url
    if (verificationResources) {
      verificationResources = verificationResources.map(
        verificationResource => ({
          ...verificationResource,
          resourceUrl: templateMacros(
            verificationResource.resourceUrl || '',
            macros
          ),
          verificationParameters: templateMacros(
            (verificationResource.verificationParameters as string) || '',
            macros
          )
        })
      );
    }

    return verificationResources;
  }

  getEndScreen(): any {
    return this._vastParser.getEndScreen();
  }

  setMuted(isMuted: boolean): void {
    if (!(window as any).isOgyPreview) {
      this._vastTracker.setMuted(isMuted);
    }
  }

  isMuted(): boolean {
    return this._vastTracker.muted;
  }

  pause(): void {
    if (!(window as any).isOgyPreview) {
      this._vastTracker.pause();
    }
  }

  resume(): void {
    if (!(window as any).isOgyPreview) {
      this._vastTracker.resume();
    }
  }

  skip(): void {
    if (!(window as any).isOgyPreview) {
      this._vastTracker.skip();
    }
  }

  click(url: string = ''): string | null {
    if ((window as any).isOgyPreview) return url;
    return this._vastTracker.click(url);
  }

  getClickEndScreenUrl(endScreen: any): string | null {
    if ((window as any).isOgyPreview) {
      const clickThrough =
        endScreen.clickThrough.length > 0 && endScreen.clickThrough[0];
      return clickThrough || '';
    }
    return this._vastTracker.getClickEndScreenUrl(endScreen);
  }

  complete(): void {
    if (!(window as any).isOgyPreview) {
      this._vastTracker.complete();
    }
  }

  impression(): void {
    if ((window as any).isOgyPreview) {
      RootStore.getInstance().adContainerStore.triggerOnAdImpression();
      return;
    }

    this._vastTracker.impression();
  }

  creativeView(): void {
    if (!(window as any).isOgyPreview) {
      this._vastTracker.creativeView();
    }
  }

  setProgress(progress: number): void {
    if (!(window as any).isOgyPreview) {
      this._vastTracker.setProgress(progress);
    }
  }

  onError(err: VASTError): void {
    if (!(window as any).isOgyPreview) {
      this._vastTracker.errorWithCode(err.code);
    }
  }

  close(): Promise<void> {
    if ((window as any).isOgyPreview) return Promise.resolve();

    return new Promise(resolve => {
      // VAST 3.0
      this._vastTracker.on('closeLinear', () => {
        resolve();
      });

      // VAST 2.0
      this._vastTracker.on('close', () => {
        resolve();
      });

      this._vastTracker.close();
    });
  }

  onEvent(cb: (vastEvent: string) => any): void {
    if ((window as any).isOgyPreview) {
      return;
    }

    supportedEvents.forEach(event =>
      this._vastTracker.on(event, cb.bind(null, event))
    );
  }

  hasAdSystem(adSystem: string): boolean {
    return this._vastParser.hasAdSystem(adSystem);
  }

  getFetchedRawVasts(): any[] {
    return (
      (this._vastParser &&
        (this._vastParser._vastLibraryParser as any) &&
        (this._vastParser._vastLibraryParser as any).getVastsRaw()) ||
      []
    );
  }
}
